<template>
  <section
    :id="`${cms.scroll_id}`"
    v-editable="cms"
    :class="[name, spacing]"
    :style="mainCssVars"
  >
    <grid
      :class="['grid--container', `${name}__grid`]"
      :layout="[
        {
          size: 1,
          columns: 'repeat(12, 1fr)',
          gap: '6px',
        },
        {
          size: 2,
          columns: 'repeat(24, 1fr)',
          gap: '10px',
        },
      ]"
    >
      <grid-item
        :class="`${name}__grid-item`"
        :layout="[
          { size: 1, x: 1, width: 12 },
          { size: 3, x: 1, width: 24 },
        ]"
      >
        <a-copy :cms="cms" :class="`${name}__content`" />

        <fetch-story
          :route="cms.api_endpoint"
          :params="{
            sort_by: cms.api_order || null,
            by_uuids: cms.api_filter || null,
          }"
          :limit="parseInt(cms.api_limit) || 0"
          :offset="parseInt(cms.api_offset) || 0"
          :collection="cms.collection"
          entry="content"
        >
          <div slot-scope="{ collection }" :class="`${name}__practices`">
            <component
              :is="item.component"
              v-for="(item, index) in collection"
              :key="index"
              v-editable="item"
              :cms="item"
              :class="`${name}__practice`"
              @onPracticeHover="onPracticeHover"
            />
          </div>
        </fetch-story>
        <transition name="fade">
          <div :class="[`${name}__description`, cssClassVisible]">
            <a-heading html-tag="h6" :cms="{ heading }" />
            <a-copy :cms="{ copy }" />
          </div>
        </transition>
      </grid-item>
    </grid>
  </section>
</template>

<script>
import data from '@/components/organisms/o-list-practices/o-list-practices-v2.data'
import MCardPractice from '@/components/molecules/m-cards/m-card-practice'
import Colors from '~/mixins/colors'
import FetchStory from '~/components/shared/fetch-story'

export default {
  components: {
    FetchStory,
    MCardPractice,
  },
  mixins: [Colors],
  props: {
    cms: {
      type: Object,
      default() {
        const config = {}
        return process.env.NODE_ENV === 'development'
          ? { ...data, ...config }
          : config
      },
    },
  },

  data() {
    return {
      name: 'o-list-practices-v2',
      visible: false,
      heading: '...',
      copy: '...',
    }
  },

  computed: {
    cssClassVisible() {
      return this.visible ? `visible` : null
    },
    spacing() {
      return this.cms.spacing
    },
    mainCssVars() {
      return {
        '--bg-color': this.setColor(
          (this.cms.color_background || {}).color || '#000000',
          this.cms.color_gradient
        ),
        '--text-color': this.setColor(
          (this.cms.color_text || {}).color || '#FFFFFF'
        ),
        '--hover-color': this.setColor(
          (this.cms.color_hover || {}).color || '#C33CA8'
        ),
        '--disabled-color': this.setColor(
          (this.cms.color_disabled || {}).color || '#888b8d'
        ),
        '--filter': this.cms.filter || null,
        '--bg-color-logo': this.setColor(
          (this.cms.logo_background_color || {}).color || '',
          this.cms.color_gradient
        ),
        '--text-color-logo': this.setColor(
          (this.cms.logo_color_text || {}).color || '#000000'
        ),
        '--hover-color-logo': this.setColor(
          (this.cms.logo_color_text_hover || {}).color || ''
        ),
        '--hover-bg-color-logo': this.setColor(
          (this.cms.logo_background_color_hover || {}).color || ''
        ),
      }
    },
  },
  methods: {
    onPracticeHover(values) {
      this.visible = values.active
      this.heading = values.heading || ''
      this.copy = values.copy || ''
    },
  },
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.o-list-practices-v2 {
  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out,
    border 0.2s ease-in-out;
  background: var(--bg-color);
  border-color: var(--text-color);
  color: var(--text-color);
  padding: 155px 0;

  ::v-deep a,
  ::v-deep .a-cta {
    color: var(--text-color);
    &:hover,
    &:active {
      color: var(--hover-color);
    }
    &:disabled,
    &.disabled {
      color: var(--disabled-color);
    }
  }

  ::v-deep img {
    filter: var(--filter);
  }

  &__practices {
    margin-top: 100px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }

  &__practice {
    ::v-deep img {
      transition: background-color 0.2s ease-in-out, filter 0.2s ease-in-out;
      &:hover {
        filter: invert(1);
        background: var(--bg-color);
      }
    }
  }

  &__description {
    display: none;
  }
}

@include media-breakpoint-up(md) {
  .o-list-practices-v2 {
    &__practices {
      margin-top: 100px;
      grid-template-columns: repeat(3, 1fr);
    }
  }
}

@include media-breakpoint-up(lg) {
  .o-list-practices-v2 {
    &__description {
      display: block;
      visibility: hidden;
      margin-top: 20px;
      &.visible {
        visibility: visible;
      }
      ::v-deep * {
        @include font-size(16, 28px);
      }
    }
    &__practices {
      grid-template-columns: repeat(6, 1fr);
    }
  }
}
</style>
